import { useContext, useEffect, useRef, useState } from "react";
import { PublicFacilitiesContext } from "./context";
import {
   Alert,
   Button,
   Card,
   Divider,
   InputNumber,
   message,
   Select,
   Table,
   Tag,
   Tooltip,
   Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { getFormattedAddress } from "./functions";
import CerCodesSelect from "../../../../components/CerCodesSelect";
import { WasteCollectionFrequencyTypes } from "../../../../constants/Enums";
import { GQL_CUSTOMER_PUBLIC_AREA_FORM_UPSERT } from "../../../../apollo/mutation/customer-public-area";
import { useMutation } from "@apollo/client";
import { get } from "lodash";

const CustomerPublicFacilitiesForm = ({ taxAccounts, form, token, refetch }) => {
   const intl = useIntl();
   const { selectedItem } = useContext(PublicFacilitiesContext);
   const { waste_bin_types = [], unit_measure_types = [] } = selectedItem || {};
   const [rows, setRows] = useState([]);
   const [upsertData, { loading: upsertLoading }] = useMutation(
      GQL_CUSTOMER_PUBLIC_AREA_FORM_UPSERT
   );
   const newRowCounter = useRef(0);

   useEffect(() => {
      if (selectedItem?.selectedIndex == null || !taxAccounts || !form) {
         setRows([]);
         return;
      }
      const taxAccount = taxAccounts[selectedItem.selectedIndex];
      const group = form?.find((f) => f.tax_account_id === taxAccount?.id);

      const items = group?.items.map((item) => ({
         ...item,
         cer_code: item.cerCode,
      }));

      setRows(items ?? []);
   }, [selectedItem?.selectedIndex, form, taxAccounts]);

   if (selectedItem?.selectedIndex == null) {
      return (
         <Alert
            className="gx-mt-5 gx-text-center gx-p-3"
            type="warning"
            message={intl.formatMessage({ id: "common.no_element_selected" })}
         />
      );
   }

   const taxAccount = taxAccounts[selectedItem.selectedIndex];

   const handleChange = (rowId, field, value) => {
      setRows((prev) =>
         prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
      );
   };

   const handleAddRow = () => {
      newRowCounter.current -= 1;
      setRows((prev) => [
         ...prev,
         {
            id: newRowCounter.current,
            bin_number: null,
            bin_type: null,
            bin_capacity: null,
            um: null,
            cer_code: null,
            frequency: null,
            exposure_frequency: null,
         },
      ]);
   };

   const handleDeleteRow = (rowId) => {
      setRows((prev) => prev.filter((r) => r.id !== rowId));
   };

   const frequencyOptions = Object.keys(WasteCollectionFrequencyTypes).map(
      (val) => ({
         value: val,
         label: intl.formatMessage({
            id: `waste_collection_frequency.${val.toLowerCase()}`,
         }),
      })
   );

   const BIN_TYPE_IMAGES = {
      BIDONE: "/assets/images/form/contenitori/bidone.jpg",
      CASSONE: "/assets/images/form/contenitori/cassonetto.jpg",
      SCARRABILE: "/assets/images/form/contenitori/scarrabile.jpg",
      CISTERNA: "/assets/images/form/contenitori/cisterna.jpg",
      GABBIA: "/assets/images/form/contenitori/gabbia.jpg",
   };

   const binTypeOptions = waste_bin_types.map((val) => ({
      value: val,
      title: intl.formatMessage({ id: `wastes.types.${val}` }),
      label: (
         <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
               src={BIN_TYPE_IMAGES[val] ?? "https://picsum.photos/seed/bin/80/80"}
               alt={val}
               style={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 4,
                  flexShrink: 0,
               }}
            />
            {intl.formatMessage({ id: `wastes.types.${val}` })}
         </span>
      ),
   }));

   const umOptions = unit_measure_types.map((val) => ({ value: val, label: val }));

   const handleSave = () => {
      const invalidRows = rows
         .map((r, i) => ({ index: i + 1, r }))
         .filter(
            ({ r }) =>
               !r.bin_type || r.bin_capacity == null || r.um == null || !r.cer_code
         );

      if (invalidRows.length > 0) {
         const rowNumbers = invalidRows.map(({ index }) => index).join(", ");
         message.error(
            intl.formatMessage(
               { id: "customer_public_area.rows_missing_required_fields" },
               { rows: rowNumbers }
            )
         );
         return;
      }

      let input = {
         token,
         tax_account_id: taxAccount.id,
         rows: rows.map((r) => ({
            id: r.id > 0 ? r.id : null, // Nuovi record hanno id negativo
            bin_type: r.bin_type,
            bin_capacity: r.bin_capacity,
            um: r.um,
            cer_code: r.cer_code?.id ?? null,
            frequency: r.frequency,
            exposure_frequency: r.exposure_frequency,
            bin_number: `${r.bin_number}`,
         })),
      };

      upsertData({
         variables: {
            input,
         },
         context: {
            headers: {
               "x-rt-customer-public-token": token,
            },
         },
      })
         .then((response) => {
            const result = get(response, "data.customerPublicAreaFormUpsert", false);
            if (result) {
               message.success(
                  intl.formatMessage({ id: "common.default_mutation_success" })
               );

               refetch();
            } else {
               message.error(
                  intl.formatMessage({ id: "common.default_mutation_error" })
               );
            }
         })
         .catch((err) => {
            message.error(
               intl.formatMessage({ id: "common.default_mutation_error" })
            );
         });
   };

   const columns = [
      {
         title: intl.formatMessage({ id: "waste.bin_number" }),
         dataIndex: "bin_number",
         width: 80,
         render: (val, record) => (
            <InputNumber
               min={0}
               value={val}
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "bin_number", v)}
            />
         ),
      },
      {
         title: intl.formatMessage({ id: "waste.bin_type" }),
         dataIndex: "bin_type",
         render: (val, record) => (
            <Select
               value={val}
               options={binTypeOptions}
               optionLabelProp="title"
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "bin_type", v)}
            />
         ),
      },
      {
         title: intl.formatMessage({ id: "common.capacity" }),
         dataIndex: "bin_capacity",
         width: 90,
         render: (val, record) => (
            <InputNumber
               min={0}
               value={val}
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "bin_capacity", v)}
            />
         ),
      },
      {
         title: intl.formatMessage({ id: "common.unit_of_measure" }),
         dataIndex: "um",
         width: 100,
         render: (val, record) => (
            <Select
               value={val}
               options={umOptions}
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "um", v)}
            />
         ),
      },
      {
         title: intl.formatMessage({ id: "waste.waste_type" }),
         dataIndex: "cer_code",
         width: 220,
         render: (val, record) => (
            <CerCodesSelect
               familyCodes={[]}
               value={val?.id ?? null}
               defaultItem={val}
               style={{ width: "100%" }}
               onChange={(v, option) =>
                  handleChange(
                     record.id,
                     "cer_code",
                     v
                        ? {
                             id: v,
                             code: option.code,
                             description: option.description,
                          }
                        : null
                  )
               }
            />
         ),
      },
      {
         title: intl.formatMessage({ id: "waste.frequency" }),
         dataIndex: "frequency",
         width: 150,
         render: (val, record) => (
            <Select
               value={val}
               options={frequencyOptions}
               allowClear
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "frequency", v)}
            />
         ),
      },
      {
         title: (
            <Tooltip
               title={intl.formatMessage({ id: "waste.exposure_frequency_tooltip" })}
            >
               <span style={{ borderBottom: "1px dashed #aaa", cursor: "help" }}>
                  {intl.formatMessage({ id: "waste.exposure_frequency" })}
               </span>
            </Tooltip>
         ),
         dataIndex: "exposure_frequency",
         width: 150,
         render: (val, record) => (
            <Select
               value={val}
               options={frequencyOptions}
               allowClear
               style={{ width: "100%" }}
               onChange={(v) => handleChange(record.id, "exposure_frequency", v)}
            />
         ),
      },
      {
         title: "",
         dataIndex: "_actions",
         width: 50,
         fixed: "right",
         render: (_, record) => (
            <Button
               type="text"
               danger
               size="small"
               onClick={() => handleDeleteRow(record.id)}
            >
               ✕
            </Button>
         ),
      },
   ];

   return (
      <Card type="inner" className="gx-mt-5">
         <Tag className="gx-mb-0" color="blue">
            <Typography.Title level={5} className="gx-m-0">
               {taxAccount?.code}&nbsp;|
               <small
                  className="gx-ml-2"
                  dangerouslySetInnerHTML={{
                     __html: getFormattedAddress(taxAccount, false),
                  }}
               />
            </Typography.Title>
         </Tag>
         <Divider />
         <Table
            rowKey="id"
            dataSource={rows}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            loading={upsertLoading}
         />
         <div
            className="gx-mt-3"
            style={{ display: "flex", justifyContent: "space-between" }}
         >
            <Button icon={<PlusOutlined />} onClick={handleAddRow}>
               {intl.formatMessage({ id: "common.add" })}
            </Button>
            <Button
               type="primary"
               className="gx-bg-success gx-border-success"
               onClick={handleSave}
               loading={upsertLoading}
            >
               {intl.formatMessage({ id: "common.save" })}
            </Button>
         </div>
      </Card>
   );
};

export default CustomerPublicFacilitiesForm;
